import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { BookOpen, Plus, Fish, ShipWheel, Pencil, Trash2, Undo2, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import JournalDialog from "@/components/JournalDialog";
import MediaGallery from "@/components/MediaGallery";
import { toast } from "sonner";

const ADMIN_ROLES = new Set(["super_admin", "owner_director", "operations_admin"]);

export default function Journal() {
  const t = useT();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [trash, setTrash] = useState([]);
  const [assets, setAssets] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTrash, setShowTrash] = useState(false);

  const reload = async () => {
    const [l, a, r, tr] = await Promise.all([
      api.get("/daily-logs"),
      api.get("/assets"),
      api.get("/reservations", { params: { status: "approved" } }),
      api.get("/daily-logs/trash").catch(() => ({ data: [] })),
    ]);
    setLogs(l.data); setAssets(a.data); setReservations(r.data); setTrash(tr.data); setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const filtered = filter === "all" ? logs : logs.filter(l => l.log_type === filter);

  const canEdit = (log) => ADMIN_ROLES.has(user?.role) || user?.id === log.skipper_user_id;

  const handleDelete = async (log) => {
    if (!window.confirm(t("journal.deleteConfirm"))) return;
    try {
      await api.delete(`/daily-logs/${log.id}`);
      toast.success(t("journal.deletedToast"));
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleRestore = async (log) => {
    try {
      await api.post(`/daily-logs/${log.id}/restore`);
      toast.success(t("journal.restoredToast"));
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handlePurgeNow = async (log) => {
    if (!window.confirm(t("journal.purgeConfirm"))) return;
    try {
      await api.delete(`/daily-logs/${log.id}/purge`);
      toast.success(t("journal.purgedToast"));
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="journal-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("journal.subtitle")}</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{t("journal.title")}</h2>
        </div>
        <div className="flex items-center gap-2">
          {trash.length > 0 && (
            <Button onClick={() => setShowTrash(v => !v)} data-testid="trash-toggle-btn" variant="outline"
              className="bg-[#0B1221] border-[#1E293B] hover:border-[#F59E0B]/50">
              <Trash className="w-4 h-4 mr-1" /> {t("journal.trashBtn")} <span className="ml-2 badge-pill badge-neutral">{trash.length}</span>
            </Button>
          )}
          <Button onClick={() => setShowNew(true)} data-testid="new-log-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
            <Plus className="w-4 h-4 mr-1" /> {t("journal.newEntry")}
          </Button>
        </div>
      </div>

      {showTrash && (
        <div className="ledger-card p-5 border-[#F59E0B]/30" data-testid="trash-panel">
          <div className="flex items-center gap-2 mb-3">
            <Trash className="w-4 h-4 text-[#F59E0B]" />
            <div className="font-mono text-[10px] tracking-widest text-[#FBBF24]">{t("journal.trashTitle")}</div>
          </div>
          <p className="text-xs text-[#94A3B8] mb-3">{t("journal.trashIntro")}</p>
          <div className="space-y-2">
            {trash.map(log => (
              <div key={log.id} className="flex items-center justify-between gap-3 p-3 bg-[#050A14] border border-[#1E293B] rounded-md" data-testid={`trash-row-${log.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{log.place_name || log.route || t("journal.untitled")}</div>
                  <div className="font-mono text-[10px] text-[#94A3B8]">
                    {t(`journal.logType.${log.log_type}`)} · {fmtDate(log.log_date)} ·
                    {" "}{t("journal.purgeOn")} {fmtDate(log.purge_after)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => handleRestore(log)} data-testid={`trash-restore-${log.id}`}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs bg-[#10B981]/15 border border-[#10B981]/40 text-[#34D399] hover:bg-[#10B981]/25">
                    <Undo2 className="w-3 h-3" /> {t("journal.restore")}
                  </button>
                  {ADMIN_ROLES.has(user?.role) && (
                    <button onClick={() => handlePurgeNow(log)} data-testid={`trash-purge-${log.id}`}
                      title={t("journal.purgeNow")}
                      className="p-1.5 rounded-md bg-[#050A14] border border-[#EF4444]/30 text-[#F87171] hover:bg-[#7F1D1D]">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="journal-filters">
        {[
          { k: "all", labelKey: "journal.filter.all" },
          { k: "captains_log", labelKey: "journal.filter.captain" },
          { k: "fishing_log", labelKey: "journal.filter.fishing" },
          { k: "excursion_log", labelKey: "journal.filter.excursion" },
          { k: "charter_log", labelKey: "journal.filter.charter" },
        ].map(({ k, labelKey }) => (
          <button key={k} onClick={() => setFilter(k)}
            data-testid={`journal-filter-${k}`}
            className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${filter === k ? "bg-[#007AFF]/15 border-[#007AFF]/50 text-[#F8FAFC]" : "bg-[#0B1221] border-[#1E293B] text-[#94A3B8] hover:border-[#334155]"}`}>
            {t(labelKey)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{Array.from({length: 4}).map((_, i) => <div key={i} className="h-24 ledger-card animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="ledger-card p-12 text-center text-[#94A3B8]">
          <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <div className="text-sm mb-1">{t("journal.empty.title")}</div>
          <div className="text-xs">{t("journal.empty.sub")}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map(l => (
            <LogCard
              key={l.id}
              log={l}
              asset={assets.find(a => a.id === l.asset_id)}
              t={t}
              canEdit={canEdit(l)}
              onEdit={() => setEditing(l)}
              onDelete={() => handleDelete(l)}
            />
          ))}
        </div>
      )}

      {showNew && (
        <JournalDialog
          open={showNew}
          onOpenChange={setShowNew}
          assets={assets}
          reservations={reservations}
          onSaved={() => { setShowNew(false); reload(); toast.success(t("journal.savedToast")); }}
        />
      )}

      {editing && (
        <JournalDialog
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          assets={assets}
          reservations={reservations}
          editing={editing}
          onSaved={() => { setEditing(null); reload(); toast.success(t("journal.savedToast")); }}
        />
      )}
    </div>
  );
}

function LogCard({ log, asset, t, canEdit, onEdit, onDelete }) {
  const isFishing = log.log_type === "fishing_log";
  return (
    <div className="ledger-card p-5" data-testid={`log-card-${log.id}`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className={`badge-pill ${isFishing ? "badge-fishing" : "badge-neutral"}`}>
            {t(`journal.logType.${log.log_type}`)}
          </span>
          {log.weather && <span className="font-mono text-[10px] text-[#94A3B8] truncate">{log.weather}</span>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="font-mono text-[11px] text-[#94A3B8] mr-1">{fmtDate(log.log_date)}</span>
          {canEdit && (
            <>
              <button onClick={onEdit} data-testid={`log-edit-${log.id}`} title={t("common.edit")}
                className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] text-[#94A3B8] hover:text-[#4DA6FF] hover:border-[#4DA6FF]/40">
                <Pencil className="w-3 h-3" />
              </button>
              <button onClick={onDelete} data-testid={`log-delete-${log.id}`} title={t("common.delete")}
                className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] text-[#94A3B8] hover:text-[#F87171] hover:border-[#EF4444]/40">
                <Trash2 className="w-3 h-3" />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="font-display text-lg font-bold tracking-tight mb-1">{log.place_name || log.route || t("journal.untitled")}</div>
      <div className="font-mono text-[11px] text-[#94A3B8] mb-3 flex items-center gap-2">
        <ShipWheel className="w-3 h-3" /> {asset?.name || "—"} · {log.skipper_name || "—"}
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        {log.fuel_liters != null && <Metric label={t("journal.metric.fuel")} value={`${log.fuel_liters}L`} />}
        {log.engine_hours != null && <Metric label={t("journal.metric.hrs")} value={log.engine_hours} />}
        {log.sea_state && <Metric label={t("journal.metric.sea")} value={log.sea_state} />}
      </div>
      {isFishing && (
        <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-md p-3 mb-3">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-[#A78BFA] mb-2">
            <Fish className="w-3 h-3" /> {t("journal.metric.fishing")}
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {log.caught_species && <div><span className="text-[#94A3B8]">{t("journal.metric.species")}:</span> {log.caught_species}</div>}
            {log.bait_used && <div><span className="text-[#94A3B8]">{t("journal.metric.bait")}:</span> {log.bait_used}</div>}
            {log.method_used && <div><span className="text-[#94A3B8]">{t("journal.metric.method")}:</span> {log.method_used}</div>}
            {log.kept_count != null && <div><span className="text-[#94A3B8]">{t("journal.metric.kept")}:</span> {log.kept_count}</div>}
            {log.released_count != null && <div><span className="text-[#94A3B8]">{t("journal.metric.released")}:</span> {log.released_count}</div>}
            {log.success_rating != null && <div><span className="text-[#94A3B8]">{t("journal.metric.rating")}:</span> {"★".repeat(log.success_rating)}</div>}
          </div>
        </div>
      )}
      {log.incidents && <Quote label={t("journal.label.incidents")} value={log.incidents} tone="red" />}
      {log.dos_note && <Quote label={t("journal.label.dos")} value={log.dos_note} tone="emerald" />}
      {log.donts_note && <Quote label={t("journal.label.donts")} value={log.donts_note} tone="amber" />}
      {(log.restaurant_review || log.bar_review) && (
        <div className="space-y-1 mt-3">
          {log.restaurant_review && <div className="text-xs"><span className="text-[#94A3B8] font-mono text-[10px] tracking-widest">{t("journal.label.restaurant")} · </span>{log.restaurant_review}</div>}
          {log.bar_review && <div className="text-xs"><span className="text-[#94A3B8] font-mono text-[10px] tracking-widest">{t("journal.label.bar")} · </span>{log.bar_review}</div>}
        </div>
      )}
      {log.guest_experience_note && <Quote label={t("journal.label.guestExperience")} value={log.guest_experience_note} tone="blue" />}
      <MediaGallery linkedType="daily_log" linkedId={log.id} allowUpload />
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-[#050A14] border border-[#1E293B] rounded-md p-2">
      <div className="font-mono text-[9px] tracking-widest text-[#94A3B8]">{label}</div>
      <div className="font-display text-base font-bold">{value}</div>
    </div>
  );
}

function Quote({ label, value, tone }) {
  const toneMap = { red: "border-l-[#EF4444]", emerald: "border-l-[#10B981]", amber: "border-l-[#F59E0B]", blue: "border-l-[#4DA6FF]" };
  return (
    <div className={`border-l-2 ${toneMap[tone]} pl-3 my-2`}>
      <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{label.toUpperCase()}</div>
      <div className="text-xs text-[#F8FAFC]">{value}</div>
    </div>
  );
}
